import type { ReconciliationAccount, ReportRequestBody } from '@query'
import { useReportEntries } from '@query'
import React, { useEffect, useMemo, useState } from 'react'
import { ReconciliationEntriesPanel } from './ReconciliationEntriesPanel'
import { drillEntryParams } from './drillEntryParams'

type Props = {
  slug: string
  reportBody: ReportRequestBody
  accountNumber: string
  currency?: string
}

export const ReconciliationAccountEntriesPanel: React.FC<Props> = ({
  slug,
  reportBody,
  accountNumber,
  currency
}) => {
  const [page, setPage] = useState(1)

  useEffect(() => {
    setPage(1)
  }, [accountNumber])

  const params = useMemo(
    () => drillEntryParams(reportBody, { account_number: accountNumber }, page),
    [reportBody, accountNumber, page]
  )

  const { data, isFetching, isLoading } = useReportEntries({
    slug,
    params,
    enabled: Boolean(slug && accountNumber)
  })

  const account: ReconciliationAccount = useMemo(
    () => ({
      number: data?.meta.account_number ?? accountNumber,
      name: data?.meta.account_name ?? '',
      opening_balance: data?.meta.page_opening_balance ?? data?.meta.opening_balance ?? 0,
      closing_balance: data?.meta.closing_balance ?? 0,
      entries: (data?.results ?? []).map(entry => ({
        number: entry.number,
        date: entry.date,
        description: entry.description,
        amount: entry.amount ?? 0,
        document_id: entry.document_id
      })),
      entries_count: data?.count ?? 0
    }),
    [data, accountNumber]
  )

  const showLoading = isLoading || isFetching

  return (
    <ReconciliationEntriesPanel
      account={account}
      attachmentsByDocument={data?.attachments_by_document}
      currency={currency}
      isLoading={showLoading}
      entriesPage={page}
      entriesPageCount={data?.page_count ?? 1}
      entriesTotalCount={data?.count ?? 0}
      entriesPageSize={data?.page_size ?? params.page_size}
      onEntriesPageChange={setPage}
    />
  )
}
